import { Settings } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type VolumeByPriceDisplayMode = 'total' | 'bySide' | 'byDate';

const STORAGE_KEY = 'mf_volume_by_price_display_mode';
const MENU_WIDTH_PX = 208;
const VIEWPORT_PAD = 8;

export function readVolumeByPriceDisplayMode(): VolumeByPriceDisplayMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'bySide' || raw === 'byDate' || raw === 'total') return raw;
  } catch {
    /* ignore */
  }
  return 'total';
}

export function persistVolumeByPriceDisplayMode(mode: VolumeByPriceDisplayMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

const DISPLAY_MODE_OPTIONS: Array<{
  value: VolumeByPriceDisplayMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'total',
    label: 'Total volume',
    hint: 'Single-color profile by traded size',
  },
  {
    value: 'bySide',
    label: 'Volume by side',
    hint: 'Buy vs sell aggressor share at each price',
  },
  {
    value: 'byDate',
    label: 'Volume by date',
    hint: 'Which session days traded at each price',
  },
];

function menuPosition(anchor: DOMRect): { left: number; top: number } {
  let left = anchor.left;
  let top = anchor.bottom + 6;
  left = Math.max(
    VIEWPORT_PAD,
    Math.min(left, window.innerWidth - MENU_WIDTH_PX - VIEWPORT_PAD),
  );
  top = Math.max(VIEWPORT_PAD, top);
  return { left, top };
}

export function VolumeByPriceLayerSettings({
  layerActive,
  displayMode,
  hasSideAttribution,
  hasDateAttribution,
  dateLegend,
  onDisplayModeChange,
}: {
  layerActive: boolean;
  displayMode: VolumeByPriceDisplayMode;
  hasSideAttribution: boolean;
  hasDateAttribution: boolean;
  /** Chronological session dates in the visible profile range. */
  dateLegend?: string[];
  onDisplayModeChange: (mode: VolumeByPriceDisplayMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ left: number; top: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const syncMenuPosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    setMenuCoords(menuPosition(el.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    syncMenuPosition();
  }, [open, syncMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, syncMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const attachId = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(attachId);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!layerActive) return null;

  const menu =
    open && menuCoords
      ? createPortal(
          <div
            ref={menuRef}
            role="dialog"
            aria-label="Volume by price display"
            className="fixed z-[200] rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
            style={{
              left: menuCoords.left,
              top: menuCoords.top,
              width: MENU_WIDTH_PX,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              Display
            </p>
            <ul className="space-y-0.5">
              {DISPLAY_MODE_OPTIONS.map((opt) => {
                const disabled =
                  (opt.value === 'bySide' && !hasSideAttribution) ||
                  (opt.value === 'byDate' && !hasDateAttribution);
                const selected = displayMode === opt.value;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      disabled={disabled}
                      title={
                        disabled
                          ? opt.value === 'byDate'
                            ? 'Need multiple sessions in range'
                            : 'No buy/sell attribution for this range'
                          : undefined
                      }
                      onClick={() => {
                        if (disabled) return;
                        onDisplayModeChange(opt.value);
                        persistVolumeByPriceDisplayMode(opt.value);
                        setOpen(false);
                      }}
                      className={`w-full rounded-md px-2 py-1.5 text-left transition-colors ${
                        disabled
                          ? 'cursor-not-allowed opacity-45'
                          : selected
                            ? 'bg-sky-600 text-white'
                            : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="block text-xs font-medium">{opt.label}</span>
                      <span
                        className={`block text-[10px] ${
                          selected && !disabled ? 'text-sky-100' : 'text-slate-500'
                        }`}
                      >
                        {opt.hint}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {displayMode === 'bySide' ? (
              <div className="mt-2 flex items-center gap-2 border-t border-slate-100 px-2 pt-2 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <span
                    className="h-2 w-3 rounded-sm"
                    style={{ backgroundColor: '#10b981', opacity: 0.85 }}
                    aria-hidden
                  />
                  Buy
                </span>
                <span className="inline-flex items-center gap-1">
                  <span
                    className="h-2 w-3 rounded-sm"
                    style={{ backgroundColor: '#f43f5e', opacity: 0.85 }}
                    aria-hidden
                  />
                  Sell
                </span>
              </div>
            ) : null}
            {displayMode === 'byDate' && dateLegend && dateLegend.length > 0 ? (
              <div className="mt-2 border-t border-slate-100 px-2 pt-2 text-[10px] text-slate-500">
                <p className="mb-1">Older sessions → newer (left to right in each bar)</p>
                <p className="text-slate-400">
                  {dateLegend[0]} … {dateLegend[dateLegend.length - 1]} · {dateLegend.length}{' '}
                  days
                </p>
                <p className="mt-1 text-slate-400">Hover a segment for date and share</p>
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Volume by price display mode"
        aria-expanded={open}
        aria-haspopup="dialog"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          syncMenuPosition();
          setOpen(true);
        }}
        className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 ${
          open || displayMode !== 'total'
            ? 'bg-sky-50 text-sky-800'
            : 'text-sky-700 hover:bg-sky-50'
        }`}
      >
        <Settings className="h-3.5 w-3.5 pointer-events-none" />
      </button>
      {menu}
    </>
  );
}
